import {
	type AriaAttributes,
	type ForwardedRef,
	forwardRef,
	type PropsWithChildren,
	useCallback,
	useState
} from 'react';
import { type FieldError } from 'react-hook-form';
import styled, { useTheme } from 'styled-components';

import { useRTL } from '../../../../hooks/helpers/useRTL';
import { InteractionLayer } from '../../../atoms/interaction-layer/InteractionLayer';
import { CheckBoldIcon } from '../../../icons';

export type BaseCheckboxInputProps = PropsWithChildren<
	Pick<AriaAttributes, 'aria-labelledby'> & {
		columnAlign?: string;
		disabled?: boolean;
		error?: FieldError;
		isStateHandledOutside?: boolean;
		onChange: (value: boolean) => void;
		testID: string;
		title?: string;
		value?: boolean;
	}
>;

/**
 *
 * @param {BaseCheckboxInputProps} props - The component's props
 * @param {string} children - Element Label
 * @param {string} disabled - is the checkbox disabled
 * @param {FieldError} error - can be used to activate the on-error style
 * @param {function} onChange - on box click change callback
 * @param {function} value - box value
 * @param {boolean} isStateHandledOutside - whether component is not handling checked state itself (using value props instead)
 */
export const BaseCheckboxInput = forwardRef(function BaseCheckboxInput(
	{
		'aria-labelledby': ariaLabelledBy,
		children,
		columnAlign = 'flex-start',
		disabled,
		error,
		isStateHandledOutside = false,
		onChange,
		testID,
		title,
		value
	}: BaseCheckboxInputProps,
	forwardedRef: ForwardedRef<HTMLDivElement>
) {
	const { colors, sizing } = useTheme();
	const { isRTL } = useRTL();
	const [isChecked, setIsChecked] = useState<boolean>(value || false);

	const handleCheckbox = useCallback(() => {
		if (!isStateHandledOutside) {
			setIsChecked(preValue => !preValue);
		}
		onChange(!isChecked);
	}, [isChecked, onChange, isStateHandledOutside]);

	return (
		<S.BaseBasicCheckboxInput $isRTL={isRTL} data-testid={testID} ref={forwardedRef}>
			<p>{title}</p>
			<S.StyledColumn $columnAlign={columnAlign}>
				<InteractionLayer
					aria-label={`${testID}-checkbox`}
					aria-labelledby={ariaLabelledBy}
					disabled={disabled}
					id={`${testID}-checkbox`}
					role="checkbox"
					onClick={handleCheckbox}
				>
					<S.StyledBasicCheckboxInputSquare $isOnError={!!error} data-testid={testID + '-checkbox'}>
						{((isStateHandledOutside && value) || (!isStateHandledOutside && isChecked)) && (
							<CheckBoldIcon
								color={colors.text.brand.enabled}
								height={sizing.xsmall}
								width={sizing.xsmall}
							/>
						)}
					</S.StyledBasicCheckboxInputSquare>
				</InteractionLayer>
				<span>{children}</span>
			</S.StyledColumn>
		</S.BaseBasicCheckboxInput>
	);
});
namespace S {
	export const BaseBasicCheckboxInput = styled.div<{ $isRTL: boolean }>`
		direction: ${({ $isRTL }) => ($isRTL ? 'rtl' : 'ltr')};
		${({ theme }) => theme.fonts.text.text3};
		display: flex;
	`;

	export const StyledBasicCheckboxInputSquare = styled.div<{ $isOnError: boolean }>`
		display: flex;
		align-items: center;
		border: ${({ $isOnError, theme }) =>
			`solid ${theme.border.width.large} ${
				$isOnError ? theme.colors.borders.error.enabled : theme.colors.borders.brand.enabled
			}`};
		${({ theme }) => theme.border.radius.small};
		width: ${({ theme }) => theme.sizing.small};
		height: ${({ theme }) => theme.sizing.small};
		cursor: pointer;
		margin-top: 2px;
	`;
	export const StyledColumn = styled.div<{ $columnAlign: string }>`
		display: flex;
		flex-direction: row;
		align-items: ${({ $columnAlign }) => $columnAlign};
		gap: ${({ theme }) => theme.sizing.xxxsmall};
	`;
}
